import React, { useState } from "react";
import EditIcon from "@mui/icons-material/Edit";
import {
  Box,
  Button,
  Container,
  DialogActions,
  DialogContent,
  Divider,
  IconButton,
  List,
  ListItem,
  ListItemSecondaryAction,
  ListItemText,
  Stack,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import CommonDialog from "app/components/common/Dialogs/CommonDialog";
import TextFieldLabel from "app/components/common/TextFieldLabel";
import { CustomTextField } from "assets/styles/globalStyledComponent";
import { toast } from "react-toastify";
import { useMutation, useQuery, useQueryClient } from "react-query";
import { useSelector } from "react-redux";
import { selectSelectedSchoolId } from "app/modules/schools/selectors";
import {
  getParentDeclarationConfig,
  upsertParentDeclarationConfig,
} from "app/services/student-management";

const ParentDeclaration = () => {
  const selectedSchoolId = useSelector(selectSelectedSchoolId);
  const queryClient = useQueryClient();

  const [parentDeclarationPoints, setParentDeclarationPoints] = useState([]);
  const [pointEditOpen, setPointEditOpen] = useState(false);
  const [pointEditIndex, setPointEditIndex] = useState(null);
  const [pointEditL1, setPointEditL1] = useState("");
  const [pointEditL2, setPointEditL2] = useState("");

  const { isLoading } = useQuery(
    ["parentDeclarationConfig", selectedSchoolId],
    () => getParentDeclarationConfig(),
    {
      enabled: Boolean(selectedSchoolId),
      onSuccess: (res) => {
        const points = res?.data?.points;
        if (Array.isArray(points)) {
          setParentDeclarationPoints(points.map((point) => ({ ...point })));
        } else {
          setParentDeclarationPoints([]);
        }
      },
      onError: (err) => {
        toast.error(
          err?.response?.data?.message ?? "Failed to load parent declaration",
        );
      },
    },
  );

  const { mutate: saveParentDeclarationMutate, isLoading: saveLoading } =
    useMutation(upsertParentDeclarationConfig, {
      onSuccess: (res, variables) => {
        toast.success(res?.data?.message ?? "Saved");
        if (variables?.points) {
          setParentDeclarationPoints(
            variables.points.map((point) => ({ ...point })),
          );
        }
        queryClient.invalidateQueries(["parentDeclarationConfig"]);
        setPointEditOpen(false);
        setPointEditIndex(null);
      },
      onError: (err) => {
        toast.error(err?.response?.data?.message ?? "Save failed");
      },
    });

  const truncateDeclarationPreview = (text, max = 100) => {
    if (!text) return "";
    const trimmedText = String(text).trim();
    return trimmedText.length <= max
      ? trimmedText
      : `${trimmedText.slice(0, max)}...`;
  };

  const handleOpenAddDeclarationPoint = () => {
    setPointEditIndex("new");
    setPointEditL1("");
    setPointEditL2("");
    setPointEditOpen(true);
  };

  const handleOpenEditDeclarationPoint = (index) => {
    const point = parentDeclarationPoints[index] || {};
    setPointEditIndex(index);
    setPointEditL1(point.language1 ?? "");
    setPointEditL2(point.language2 ?? "");
    setPointEditOpen(true);
  };

  const handleClosePointEditDialog = () => {
    setPointEditOpen(false);
    setPointEditIndex(null);
  };

  const handleSavePointEditDialog = () => {
    const trimmed1 = pointEditL1.trim();
    const trimmed2 = pointEditL2.trim();

    if (!trimmed1 || !trimmed2) {
      toast.error("Language 1 and Language 2 are required.");
      return;
    }

    const nextPoint = { language1: trimmed1, language2: trimmed2 };
    let nextPoints = [];

    if (pointEditIndex === "new") {
      nextPoints = [...parentDeclarationPoints, nextPoint];
    } else if (typeof pointEditIndex === "number") {
      nextPoints = parentDeclarationPoints.map((point, index) =>
        index === pointEditIndex ? nextPoint : point,
      );
    } else {
      return;
    }

    saveParentDeclarationMutate({ points: nextPoints });
  };

  return (
    <Container maxWidth="xl">
      <Stack
        direction={{ xs: "column", sm: "row" }}
        alignItems={{ xs: "stretch", sm: "center" }}
        justifyContent="space-between"
        gap={1.5}
        sx={{ mt: "2rem", mb: "1rem" }}
      >
        <Typography sx={{ fontSize: "18px", fontWeight: 600 }}>
          Parent Declaration
        </Typography>

        <LoadingButton
          variant="outlined"
          color="secondary"
          size="large"
          onClick={handleOpenAddDeclarationPoint}
          sx={{ textTransform: "none", borderRadius: "6px", px: 2 }}
        >
          Add Declaration
        </LoadingButton>
      </Stack>

      <Box
        sx={{
          border: "1px solid",
          borderColor: "divider",
          borderRadius: "8px",
          minHeight: "220px",
          bgcolor: "background.paper",
        }}
      >
        {isLoading ? (
          <Typography sx={{ p: 2 }}>Loading declarations...</Typography>
        ) : parentDeclarationPoints.length === 0 ? (
          <Typography sx={{ p: 2, color: "text.secondary" }}>
            No declarations yet. Use Add Declaration to create one.
          </Typography>
        ) : (
          <List dense disablePadding>
            {parentDeclarationPoints.map((row, index) => (
              <React.Fragment key={index}>
                <ListItem sx={{ alignItems: "flex-start", pr: 6, py: 1.5 }}>
                  <ListItemText
                    primary={`Declaration ${index + 1}`}
                    primaryTypographyProps={{ variant: "subtitle2" }}
                    secondary={
                      <Stack spacing={0.5} sx={{ mt: 0.5 }}>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            Language 1:
                          </Box>
                          {truncateDeclarationPreview(row.language1)}
                        </Typography>
                        <Typography
                          variant="body2"
                          color="text.secondary"
                          component="div"
                        >
                          <Box component="span" sx={{ fontWeight: 600 }}>
                            Language 2:
                          </Box>
                          {truncateDeclarationPreview(row.language2)}
                        </Typography>
                      </Stack>
                    }
                    secondaryTypographyProps={{ component: "div" }}
                  />
                  <ListItemSecondaryAction>
                    <IconButton
                      edge="end"
                      aria-label="edit declaration"
                      onClick={() => handleOpenEditDeclarationPoint(index)}
                      size="small"
                    >
                      <EditIcon fontSize="small" />
                    </IconButton>
                  </ListItemSecondaryAction>
                </ListItem>
                {index !== parentDeclarationPoints.length - 1 && <Divider />}
              </React.Fragment>
            ))}
          </List>
        )}
      </Box>

      <CommonDialog
        open={pointEditOpen}
        onClose={handleClosePointEditDialog}
        minWidth="480px"
        title={
          pointEditIndex === "new" ? "Add declaration" : "Edit declaration"
        }
      >
        <DialogContent>
          <Stack spacing={1.5} sx={{ pt: 1 }}>
            <TextFieldLabel title="Language 1" />
            <CustomTextField
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              placeholder="Enter Language 1"
              value={pointEditL1}
              onChange={(e) => setPointEditL1(e.target.value)}
              InputProps={{
                sx: { "& textarea": { maxHeight: 72, overflow: "auto" } },
              }}
            />
            <TextFieldLabel title="Language 2" />
            <CustomTextField
              multiline
              minRows={2}
              maxRows={4}
              fullWidth
              size="small"
              placeholder="Enter Language 2"
              value={pointEditL2}
              onChange={(e) => setPointEditL2(e.target.value)}
              InputProps={{
                sx: { "& textarea": { maxHeight: 72, overflow: "auto" } },
              }}
            />
          </Stack>
        </DialogContent>
        <Divider />
        <DialogActions sx={{ px: 2, pb: 2, pt: 1, gap: 1 }}>
          <Button onClick={handleClosePointEditDialog}>Cancel</Button>
          <LoadingButton
            variant="contained"
            color="secondary"
            loading={saveLoading}
            onClick={handleSavePointEditDialog}
          >
            Save
          </LoadingButton>
        </DialogActions>
      </CommonDialog>
    </Container>
  );
};

export default ParentDeclaration;
